/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldAlert, UserX, XCircle, Clock, CheckCircle } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { privacyAPI } from '../api/privacy';
import { fmtDataOra } from '../utils/date';

const ACCENT = '#8B5CF6';

const STATO_STYLE = {
  in_attesa: { bg: '#FEF3C7', color: '#92400E', label: 'In attesa' },
  completata: { bg: '#D1FAE5', color: '#065F46', label: 'Completata' },
  rifiutata: { bg: '#FEE2E2', color: '#991B1B', label: 'Rifiutata' },
};

export default function RichiestePrivacy() {
  const qc = useQueryClient();
  const [filtro, setFiltro] = useState('in_attesa');
  const [msg, setMsg] = useState('');

  const { data: richieste = [], isLoading } = useQuery({
    queryKey: ['richieste-privacy'],
    queryFn: () => privacyAPI.listaRichieste(),
  });

  const anonimizzaMut = useMutation({
    mutationFn: (id) => privacyAPI.anonimizza(id),
    onSuccess: () => { setMsg(''); qc.invalidateQueries({ queryKey: ['richieste-privacy'] }); },
    onError: (e) => setMsg(e.response?.data?.error || 'Errore'),
  });

  const rifiutaMut = useMutation({
    mutationFn: ({ id, note }) => privacyAPI.rifiuta(id, note),
    onSuccess: () => { setMsg(''); qc.invalidateQueries({ queryKey: ['richieste-privacy'] }); },
    onError: (e) => setMsg(e.response?.data?.error || 'Errore'),
  });

  const handleAnonimizza = (r) => {
    if (window.confirm(`Anonimizzare definitivamente i dati di ${r.cliente_nome} ${r.cliente_cognome}? L'account verrà disattivato e i dati identificativi rimossi. Gli interventi collegati restano per obblighi contabili, in forma anonima.`)) {
      anonimizzaMut.mutate(r.id);
    }
  };

  const handleRifiuta = (r) => {
    const note = window.prompt('Motivo del rifiuto (es. fatture aperte da saldare):');
    if (note === null) return;
    if (!note.trim()) { setMsg('Inserisci un motivo per il rifiuto'); return; }
    rifiutaMut.mutate({ id: r.id, note: note.trim() });
  };

  const lista = richieste.filter((r) => filtro === 'tutte' || r.stato === filtro);

  return (
    <AdminLayout title="Richieste privacy">
      <div style={styles.filters}>
        {[
          { v: 'in_attesa', l: 'In attesa' },
          { v: 'completata', l: 'Completate' },
          { v: 'rifiutata', l: 'Rifiutate' },
          { v: 'tutte', l: 'Tutte' },
        ].map((f) => (
          <button key={f.v} onClick={() => setFiltro(f.v)} style={{ ...styles.filterBtn, ...(filtro === f.v ? styles.filterActive : {}) }}>
            {f.l}
          </button>
        ))}
      </div>

      {msg && <div style={styles.errorBox}>{msg}</div>}

      {isLoading ? (
        <div style={styles.empty}>Caricamento...</div>
      ) : lista.length === 0 ? (
        <div style={styles.empty}>
          <ShieldAlert size={40} color="#9CA3AF" />
          <p>Nessuna richiesta {filtro === 'tutte' ? '' : STATO_STYLE[filtro]?.label.toLowerCase()}.</p>
        </div>
      ) : (
        <div style={styles.list}>
          {lista.map((r) => {
            const s = STATO_STYLE[r.stato] || STATO_STYLE.in_attesa;
            return (
              <div key={r.id} style={styles.card}>
                <div style={styles.cardHead}>
                  <span style={{ ...styles.badge, backgroundColor: s.bg, color: s.color }}>{s.label}</span>
                  <span style={styles.tipo}>Richiesta di cancellazione account</span>
                </div>
                <div style={styles.clienteNome}>{r.cliente_nome} {r.cliente_cognome}</div>
                <div style={styles.meta}>{r.cliente_email}{r.cliente_telefono ? ` · ${r.cliente_telefono}` : ''}</div>
                <div style={styles.meta}><Clock size={13} /> Richiesta il {fmtDataOra(r.created_at)}</div>
                {r.stato !== 'in_attesa' && r.gestita_il && (
                  <div style={styles.meta}><CheckCircle size={13} /> Gestita il {fmtDataOra(r.gestita_il)}</div>
                )}
                {r.note && <div style={styles.note}>Nota: {r.note}</div>}

                {r.stato === 'in_attesa' && (
                  <div style={styles.actions}>
                    <button onClick={() => handleAnonimizza(r)} disabled={anonimizzaMut.isPending} style={styles.btnDanger}>
                      <UserX size={15} /> Anonimizza cliente
                    </button>
                    <button onClick={() => handleRifiuta(r)} disabled={rifiutaMut.isPending} style={styles.btnSecondary}>
                      <XCircle size={15} /> Rifiuta
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </AdminLayout>
  );
}

const styles = {
  filters: { display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', flexWrap: 'wrap' },
  filterBtn: { padding: '0.55rem 1rem', backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', cursor: 'pointer', fontSize: '0.88rem', fontWeight: 500, fontFamily: 'inherit' },
  filterActive: { backgroundColor: ACCENT, color: 'white', borderColor: ACCENT },
  errorBox: { backgroundColor: '#FEE2E2', color: '#991B1B', padding: '0.75rem 1rem', borderRadius: '8px', fontSize: '0.88rem', marginBottom: '1rem' },
  empty: { padding: '3rem', textAlign: 'center', color: '#9CA3AF', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', backgroundColor: 'white', borderRadius: '12px' },
  list: { display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  card: { backgroundColor: 'white', padding: '1.1rem 1.25rem', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  cardHead: { display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.5rem', flexWrap: 'wrap' },
  badge: { padding: '0.2rem 0.6rem', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 700 },
  tipo: { fontSize: '0.85rem', color: '#6B7280' },
  clienteNome: { fontSize: '1.05rem', fontWeight: 700, color: '#111827' },
  meta: { display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem', color: '#4B5563', marginTop: '0.25rem' },
  note: { marginTop: '0.5rem', padding: '0.6rem 0.8rem', backgroundColor: '#F9FAFB', borderRadius: '8px', fontSize: '0.85rem', color: '#374151' },
  actions: { display: 'flex', gap: '0.6rem', marginTop: '0.9rem', flexWrap: 'wrap' },
  btnDanger: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.55rem 0.9rem', backgroundColor: '#DC2626', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' },
  btnSecondary: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.55rem 0.9rem', backgroundColor: 'white', color: '#374151', border: '1px solid #D1D5DB', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' },
};
